import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { doesPolylineIntersectPolygon } from '../utils/geometryUtils';
import { slicePolylineByFraction, nearestFractionOnPolyline, interpolateAlongCartesianPolyline, } from '../utils/polylineInterpolation';
import { disableCameraControls, enableCameraControls } from '../utils/cesiumHelpers';
import { isTerrainOn, VECTOR_LAYER_TAG, CONTOUR_HIGHLIGHT_ZINDEX } from '../utils/vectorLayerTerrainSync';
import { HIGHLIGHTED_CONTOUR_FLAG } from './artifacts/contourMonochrome';
import { useEventSubscribe } from '../events/hooks';
import { useCustomDataSource } from './common/useCustomDataSource';
const DEFAULT_HIGHLIGHT_COLOR = '#DC143C'; // Cherry red
export const computeCoordHash = (entity) => {
    var _a;
    if (!entity.polyline)
        return '';
    const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
    if (!positions || positions.length === 0)
        return '';
    const first = positions[0];
    const mid = positions[Math.floor(positions.length / 2)];
    const last = positions[positions.length - 1];
    const coords = [first, mid, last].map(p => {
        const c = Cesium.Cartographic.fromCartesian(p);
        return `${c.longitude.toFixed(6)},${c.latitude.toFixed(6)}`;
    }).join('|');
    let hash = 0;
    for (let i = 0; i < coords.length; i++) {
        hash = ((hash << 5) - hash) + coords.charCodeAt(i);
        hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
};
// Default highlight line width when the user hasn't picked one yet.
// Matches DEFAULT_LINE_WIDTH in ContourHighlightDialog so a fresh
// highlight always lands at a visible-but-modest thickness.
const DEFAULT_HIGHLIGHT_LINE_WIDTH = 3;
// Restyle-in-place: for a WHOLE highlight, recolour the source contour line
// itself (solid colour + width) instead of drawing a second line on top — so
// there is no coincident geometry to z-fight, in any view, with zero added
// cost. Mirrors the monochrome stash/restore pattern. Stores the prior
// appearance (which may itself be monochrome) so unhighlight is lossless.
const HL_SOURCE_STASH = '_lsContourHighlightSource';
const restyleSourceContour = (entity, color, width) => {
    if (!entity || !entity.polyline)
        return;
    if (entity[HL_SOURCE_STASH] === undefined) {
        entity[HL_SOURCE_STASH] = { material: entity.polyline.material, width: entity.polyline.width };
    }
    entity.polyline.material = new Cesium.ColorMaterialProperty(color);
    entity.polyline.width = new Cesium.ConstantProperty(width);
    // Claim ownership so the layer's monochrome/opacity restyle effect skips it.
    entity[HIGHLIGHTED_CONTOUR_FLAG] = true;
};
const restoreSourceContour = (entity) => {
    if (!entity || !entity.polyline || entity[HL_SOURCE_STASH] === undefined)
        return;
    entity.polyline.material = entity[HL_SOURCE_STASH].material;
    entity.polyline.width = entity[HL_SOURCE_STASH].width;
    entity[HL_SOURCE_STASH] = undefined;
    entity[HIGHLIGHTED_CONTOUR_FLAG] = false;
};
const isWholeSpan = (start, end) => start <= 0 && end >= 1;
// Partial highlight: the two drag handles live in the highlights data
// source alongside the overlay, keyed off the overlay entity id with these
// suffixes so they can be picked back out on drag / cleaned up on exit.
const HANDLE_START_SUFFIX = '::handle::start';
const HANDLE_END_SUFFIX = '::handle::end';
// Default handle positions when a contour is first switched to Partial —
// the terminal ends, so the WHOLE contour stays highlighted and the user
// drags inward from each tip rather than starting from a carved-out middle.
const DEFAULT_PARTIAL_START = 0;
const DEFAULT_PARTIAL_END = 1;
// Minimum gap between the two handles so the slice never collapses.
const MIN_PARTIAL_GAP = 0.01;
// A contour counts as closed (loop) when its endpoints sit within this
// fraction of its total length of each other — only loops offer "Flip side".
const CLOSED_LOOP_TOLERANCE = 0.02;
// Whether a highlight's fractions describe a partial (non-full) range.
const isPartialRange = (start, end) => {
    const s = start !== null && start !== void 0 ? start : 0;
    const e = end !== null && end !== void 0 ? end : 1;
    return !(s <= 0 && e >= 1);
};
// Closed-loop test: first vertex within tolerance of the last, relative to
// the polyline's total length.
const isClosedPolyline = (positions) => {
    if (positions.length < 3)
        return false;
    let total = 0;
    for (let i = 1; i < positions.length; i++) {
        total += Cesium.Cartesian3.distance(positions[i - 1], positions[i]);
    }
    if (total === 0)
        return false;
    const gap = Cesium.Cartesian3.distance(positions[0], positions[positions.length - 1]);
    return gap / total <= CLOSED_LOOP_TOLERANCE;
};
const isContourArtifact = (artifact) => {
    var _a;
    try {
        const name = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function'
            ? artifact.metadata.getName()
            : '';
        const lowerName = name.toLowerCase();
        return lowerName.includes('contour') ||
            lowerName === '0.5m' ||
            lowerName === '1m' ||
            lowerName === '2m' ||
            lowerName === '3m' ||
            lowerName.match(/^\d+(\.\d+)?m$/) !== null ||
            lowerName.match(/^\d+(\.\d+)?ft$/) !== null;
    }
    catch (_b) {
        return false;
    }
};
const extractContourIntervalFromArtifact = (artifact) => {
    var _a;
    try {
        const name = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function'
            ? artifact.metadata.getName()
            : 'Unknown';
        // Format contour filenames nicely: contours_4ft_display.kml → "4 ft"
        const contourMatch = name.match(/^contours_([0-9.]+)(ft|m)(?:_display)?\.(?:kml|dxf|geojson|shp\.zip)$/i);
        if (contourMatch) {
            const value = contourMatch[1];
            const unit = contourMatch[2].toLowerCase() === 'ft' ? 'ft' : 'm';
            return `${value} ${unit}`;
        }
        return name;
    }
    catch (_b) {
        return 'Unknown';
    }
};
const extractElevationFromEntity = (entity) => {
    var _a;
    try {
        // Try to get elevation from entity name (common for KML contours)
        const name = entity.name;
        if (name) {
            // Check if name contains elevation value (e.g., "100", "100m", "100 m", "100ft")
            const elevMatch = name.match(/^[\d.]+\s*(m|ft)?$/i);
            if (elevMatch) {
                return name.trim();
            }
        }
        // Try to get from entity properties
        if (entity.properties) {
            const propNames = entity.properties.propertyNames;
            for (const propName of propNames) {
                const lowerName = propName.toLowerCase();
                if (lowerName === 'elevation' || lowerName === 'elev' || lowerName === 'altitude' || lowerName === 'height' || lowerName === 'z') {
                    const value = (_a = entity.properties[propName]) === null || _a === void 0 ? void 0 : _a.getValue();
                    if (value !== undefined && value !== null) {
                        return String(value);
                    }
                }
            }
        }
        // Try to extract from description
        if (entity.description) {
            // Cesium's typings declare Entity.description as a string, but the
            // runtime descriptor wraps it in a Property, so read it either way.
            const description = entity.description;
            const desc = typeof description === 'string'
                ? description
                : description.getValue();
            if (typeof desc === 'string') {
                // Look for elevation patterns in description
                const elevPattern = /(?:elevation|elev|altitude|height)[\s:=]+(-?[\d.]+)\s*(m|ft|meters|feet)?/i;
                const match = desc.match(elevPattern);
                if (match) {
                    return match[2] ? `${match[1]} ${match[2]}` : match[1];
                }
            }
        }
        return null;
    }
    catch (_b) {
        return null;
    }
};
export const useContourHighlight = ({ viewer, loadedArtifacts, isArtifactVisible, constraintPolygon, isActive = true }) => {
    const [highlightedContours, setHighlightedContours] = useState(new Map());
    const [pendingClick, setPendingClick] = useState(null);
    const [hoveredContour, setHoveredContour] = useState(null);
    // Snapshot of the highlight as it existed when the user clicked it
    // for editing. Used to revert all attributes (colour, width, name)
    // on cancel — the previewing flow modifies the highlight in place.
    const editBaselineRef = useRef(null);
    const [pendingRestores, setPendingRestores] = useState([]);
    const [contourVisibility, setContourVisibility] = useState(new Map());
    const [contourOpacities, setContourOpacities] = useState(new Map());
    // Terrain-mode sync: highlight lines must clamp/unclamp to terrain together
    // with the base contour layers they trace, or they float off the draped
    // contours whenever 3D terrain is on (see vectorLayerTerrainSync).
    const highlightsDataSourceRef = useCustomDataSource(viewer, 'contour-highlights', (ds) => {
        ds[VECTOR_LAYER_TAG] = true;
    });
    // Partial-highlight (draggable-handles) state. `partialEntityId` is the
    // overlay currently showing its two handles; null when no contour is in
    // Partial mode. The drag ref tracks which handle is being dragged. The
    // source-positions cache holds the FULL source polyline per entity so the
    // slice + projection math doesn't re-derive it from coordinates each move.
    const [partialEntityId, setPartialEntityId] = useState(null);
    const draggingHandleRef = useRef(null);
    // Set after the partial helpers are defined; handleMapHover (defined
    // earlier) calls through this ref so it can drive handle dragging without
    // a use-before-declaration cycle.
    const handleDragMoveRef = useRef(null);
    const partialSourcePosRef = useRef(new Map());
    // Live fractions during an interaction, so drag/flip update Cesium
    // imperatively (smooth) and only commit to React state on release.
    const partialFractionsRef = useRef(new Map());
    // Mirror of highlightedContours for use inside imperative event handlers.
    const highlightedContoursRef = useRef(highlightedContours);
    useEffect(() => { highlightedContoursRef.current = highlightedContours; }, [highlightedContours]);
    // Find which artifact and entity a contour belongs to
    const findContourInfo = useCallback((pickedEntity) => {
        var _a;
        for (const [artifactId, artifact] of loadedArtifacts.entries()) {
            if (artifact.type !== 'vector' || !isContourArtifact(artifact))
                continue;
            const dataSource = artifact.cesiumObject;
            if ((_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values) {
                for (const entity of dataSource.entities.values) {
                    if (entity === pickedEntity || entity.id === pickedEntity.id) {
                        return { artifact, artifactId };
                    }
                }
            }
        }
        return null;
    }, [loadedArtifacts]);
    // Handle mouse move for hover detection. Always runs — the cursor
    // manager wants to know about hovered highlights even when Highlight
    // Mode is off, so existing highlights remain editable on click.
    const handleMapHover = useCallback((event) => {
        var _a;
        if (!viewer || viewer.isDestroyed()) {
            setHoveredContour(null);
            return false;
        }
        // A handle drag in progress owns the cursor — drive it and skip the
        // normal hover detection below.
        if ((_a = handleDragMoveRef.current) === null || _a === void 0 ? void 0 : _a.call(handleDragMoveRef, event.endPosition)) {
            return true;
        }
        const pickedObject = viewer.scene.pick(event.endPosition);
        if (!pickedObject || !pickedObject.id) {
            setHoveredContour(null);
            return false;
        }
        const entity = pickedObject.id;
        if (!entity.polyline) {
            setHoveredContour(null);
            return false;
        }
        const contourInfo = findContourInfo(entity);
        if (!contourInfo) {
            setHoveredContour(null);
            return false;
        }
        const entityId = entity.id || '';
        const isHighlighted = highlightedContours.has(entityId);
        const highlight = highlightedContours.get(entityId);
        const contourInterval = isHighlighted && highlight
            ? highlight.contourInterval
            : extractContourIntervalFromArtifact(contourInfo.artifact);
        const contourElevation = isHighlighted && highlight
            ? highlight.contourElevation
            : extractElevationFromEntity(entity);
        setHoveredContour({
            entityId,
            contourInterval,
            contourElevation,
            screenPosition: { x: event.endPosition.x, y: event.endPosition.y },
            isHighlighted
        });
        return true;
    }, [viewer, findContourInfo, highlightedContours]);
    // Handle click on map to detect contour entities. The Highlight Mode
    // gate only applies to CREATING a new highlight — clicking an
    // already-highlighted contour to edit it works any time, so users
    // don't have to flip the toggle on just to rename or recolour.
    const handleMapClick = useCallback((event) => {
        var _a, _b, _c;
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!pickedObject || !pickedObject.id)
            return false;
        const entity = pickedObject.id;
        // Check if this is a polyline entity (contour line)
        if (!entity.polyline)
            return false;
        // Check if it's from a contour artifact
        const contourInfo = findContourInfo(entity);
        if (!contourInfo)
            return false;
        // If constraint polygon is set, validate contour intersects it
        if (constraintPolygon && constraintPolygon.length >= 3 && entity.polyline) {
            const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
            if (positions && positions.length > 0) {
                const contourCoords = positions.map((pos) => {
                    const cartographic = Cesium.Cartographic.fromCartesian(pos);
                    return {
                        lat: Cesium.Math.toDegrees(cartographic.latitude),
                        lng: Cesium.Math.toDegrees(cartographic.longitude)
                    };
                });
                if (!doesPolylineIntersectPolygon(contourCoords, constraintPolygon)) {
                    return false;
                }
            }
        }
        const contourInterval = extractContourIntervalFromArtifact(contourInfo.artifact);
        const contourElevation = extractElevationFromEntity(entity);
        // Already highlighted? Re-open the dialog in EDIT mode so the user
        // can change colour, width, or name. Snapshot the current highlight
        // attributes so cancel can restore them — preview-during-edit is
        // applied in place against the live highlight.
        const entityId = entity.id || `entity-${Date.now()}`;
        if (highlightedContours.has(entityId)) {
            const existing = highlightedContours.get(entityId);
            editBaselineRef.current = {
                entityId,
                colorHex: existing.colorHex,
                userLineWidth: existing.userLineWidth,
                name: existing.name,
                startFraction: (_b = existing.startFraction) !== null && _b !== void 0 ? _b : 0,
                endFraction: (_c = existing.endFraction) !== null && _c !== void 0 ? _c : 1,
            };
            setPendingClick({
                entity,
                contourInterval: existing.contourInterval,
                contourElevation: existing.contourElevation,
                screenPosition: { x: event.position.x, y: event.position.y },
                artifactId: existing.artifactId
            });
            return true;
        }
        // Creating a new highlight requires Highlight Mode to be on.
        // Editing an existing one (handled above) doesn't.
        if (!isActive)
            return false;
        // Set pending click — a useEffect below watches this and applies
        // the default-colour preview immediately so the user sees the
        // highlight as soon as they click (without needing to touch the
        // colour picker first).
        setPendingClick({
            entity,
            contourInterval,
            contourElevation,
            screenPosition: { x: event.position.x, y: event.position.y },
            artifactId: contourInfo.artifactId
        });
        return true;
    }, [isActive, viewer, findContourInfo, highlightedContours, constraintPolygon]);
    // Handle double-click to unhighlight. Always available — if you can
    // see the highlight, you can remove it; no Highlight Mode required
    // (matches the click-to-edit-existing-highlight behaviour).
    const handleMapDoubleClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!pickedObject || !pickedObject.id)
            return false;
        const entity = pickedObject.id;
        const entityId = entity.id;
        if (!entityId || !highlightedContours.has(entityId))
            return false;
        // Unhighlight this contour
        unhighlightContour(entityId);
        return true;
        // unhighlightContour is intentionally NOT in deps — it's defined
        // later in the function body (TDZ would throw); the closure picks
        // it up at call time, which is fine.
    }, [viewer, highlightedContours]);
    // Highlight a contour. The source contour entity is NEVER mutated —
    // the highlight is rendered as a separate overlay polyline in the
    // highlights data source, sliced to the current startFraction /
    // endFraction range. Defaults to the full contour (0 → 1).
    const highlightContour = useCallback((entity, artifactId, contourInterval, contourElevation, colorHex = DEFAULT_HIGHLIGHT_COLOR, name, startFraction = 0, endFraction = 1) => {
        var _a;
        if (!viewer || viewer.isDestroyed() || !entity.polyline)
            return;
        const entityId = entity.id || `entity-${Date.now()}`;
        const highlightColor = Cesium.Color.fromCssColorString(colorHex);
        // Capture the source polyline positions so we can render the
        // overlay (and persist coordinates for restore-from-config).
        const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
        if (!positions || positions.length < 2)
            return;
        const coordinates = positions.map((pos) => {
            const cart = Cesium.Cartographic.fromCartesian(pos);
            return {
                lat: Cesium.Math.toDegrees(cart.latitude),
                lng: Cesium.Math.toDegrees(cart.longitude)
            };
        });
        const overlayPositions = slicePolylineByFraction(positions, startFraction, endFraction);
        const highlightName = name || `Contour at ${contourElevation || 'unknown'}`;
        const highlightInfo = {
            entityId,
            entity,
            name: highlightName,
            contourInterval,
            contourElevation,
            highlightColor: highlightColor.clone(),
            colorHex,
            artifactId,
            coordinates,
            startFraction,
            endFraction
        };
        setHighlightedContours((prev) => {
            const newMap = new Map(prev);
            newMap.set(entityId, highlightInfo);
            return newMap;
        });
        // Whole highlight: recolour the source contour line itself — no overlay
        // at all, so there is nothing coincident to z-fight or float off terrain.
        // Partial replaces the source with a slice overlay (+ remainder pieces,
        // drawn by applyPartialRender during handle interaction). Any stale
        // overlay from a previous state is dropped first.
        const ds = highlightsDataSourceRef.current;
        const existing = ds === null || ds === void 0 ? void 0 : ds.entities.getById(entityId);
        if (existing)
            ds.entities.remove(existing);
        if (isWholeSpan(startFraction, endFraction)) {
            restyleSourceContour(entity, highlightColor, DEFAULT_HIGHLIGHT_LINE_WIDTH);
        }
        else {
            restoreSourceContour(entity);
            if (ds) {
                ds.entities.add({
                    id: entityId,
                    polyline: {
                        positions: overlayPositions,
                        material: new Cesium.ColorMaterialProperty(highlightColor),
                        // No depthFailMaterial: the split hides the source line, so the slice
                        // has nothing coincident to fight. The slice occludes exactly like
                        // the base contours — consistent and clean in every view.
                        width: DEFAULT_HIGHLIGHT_LINE_WIDTH,
                        // Match the base contour layers' current terrain mode; the tagged
                        // highlights data source is re-synced by the 3D Terrain toggle.
                        clampToGround: isTerrainOn(viewer),
                        arcType: Cesium.ArcType.GEODESIC,
                        zIndex: CONTOUR_HIGHLIGHT_ZINDEX
                    }
                });
            }
        }
    }, [viewer]);
    // Remove the two drag handles for a partial highlight (if present) and
    // drop the per-entity interaction caches. Safe to call unconditionally.
    const removePartialHandles = useCallback((entityId) => {
        const ds = highlightsDataSourceRef.current;
        if (ds) {
            const start = ds.entities.getById(entityId + HANDLE_START_SUFFIX);
            if (start)
                ds.entities.remove(start);
            const end = ds.entities.getById(entityId + HANDLE_END_SUFFIX);
            if (end)
                ds.entities.remove(end);
        }
        partialSourcePosRef.current.delete(entityId);
        partialFractionsRef.current.delete(entityId);
    }, []);
    // Unhighlight a contour — drop the overlay; source contour was never
    // mutated, so nothing to restore.
    const unhighlightContour = useCallback((entityId) => {
        var _a;
        if (!highlightedContours.has(entityId))
            return;
        // Restore the source contour line: re-show it (a partial split hid it),
        // and restore its style if a whole highlight had recoloured it. Drop any
        // remainder pieces from a partial split.
        const src = (_a = highlightedContours.get(entityId)) === null || _a === void 0 ? void 0 : _a.entity;
        restoreSourceContour(src);
        if (src === null || src === void 0 ? void 0 : src.polyline)
            src.polyline.show = new Cesium.ConstantProperty(true);
        if (src)
            src[HIGHLIGHTED_CONTOUR_FLAG] = false;
        if (highlightsDataSourceRef.current) {
            for (let i = 0; i < 2; i++) {
                const rem = highlightsDataSourceRef.current.entities.getById(`${entityId}::rem::${i}`);
                if (rem)
                    highlightsDataSourceRef.current.entities.remove(rem);
            }
        }
        removePartialHandles(entityId);
        if (partialEntityId === entityId)
            setPartialEntityId(null);
        if (highlightsDataSourceRef.current) {
            const overlayEntity = highlightsDataSourceRef.current.entities.getById(entityId);
            if (overlayEntity) {
                highlightsDataSourceRef.current.entities.remove(overlayEntity);
            }
        }
        setHighlightedContours((prev) => {
            const newMap = new Map(prev);
            newMap.delete(entityId);
            return newMap;
        });
        setContourVisibility(prev => { const m = new Map(prev); m.delete(entityId); return m; });
        setContourOpacities(prev => { const m = new Map(prev); m.delete(entityId); return m; });
    }, [highlightedContours, removePartialHandles, partialEntityId]);
    // Unhighlight all contours — drop every overlay entity. Source
    // contours were never mutated.
    const unhighlightAll = useCallback(() => {
        // Restore every source contour line: re-show (partial splits hid some) and
        // undo any whole-highlight recolour. removeAll() drops the slice + remainder
        // + handle entities.
        highlightedContoursRef.current.forEach((h) => {
            var _a;
            restoreSourceContour(h.entity);
            if ((_a = h.entity) === null || _a === void 0 ? void 0 : _a.polyline)
                h.entity.polyline.show = new Cesium.ConstantProperty(true);
            if (h.entity)
                h.entity[HIGHLIGHTED_CONTOUR_FLAG] = false;
        });
        if (highlightsDataSourceRef.current) {
            highlightsDataSourceRef.current.entities.removeAll();
        }
        partialSourcePosRef.current.clear();
        partialFractionsRef.current.clear();
        draggingHandleRef.current = null;
        setPartialEntityId(null);
        setHighlightedContours(new Map());
    }, []);
    // --- Partial-highlight (draggable handles) -------------------------------
    // The full source polyline for a highlight, rebuilt from its captured
    // coordinates and cached. This is what the slice + projection operate on.
    const getSourcePositions = useCallback((entityId) => {
        const cached = partialSourcePosRef.current.get(entityId);
        if (cached)
            return cached;
        const h = highlightedContoursRef.current.get(entityId);
        if (!h || h.coordinates.length < 2)
            return null;
        const positions = h.coordinates.map(c => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
        partialSourcePosRef.current.set(entityId, positions);
        return positions;
    }, []);
    // Imperatively re-draw the overlay slice and reposition the two handles
    // for the given fractions. Used by enter-partial, drag-move, flip, and
    // commit. Does not touch React state.
    const applyPartialRender = useCallback((entityId, start, end) => {
        var _a, _b;
        const ds = highlightsDataSourceRef.current;
        if (!ds || !viewer || viewer.isDestroyed())
            return;
        const positions = getSourcePositions(entityId);
        if (!positions)
            return;
        // Split, don't overlay: hide the source contour line and replace it with the
        // recoloured slice + the remainder in the source's own style — so nothing is
        // coincident to z-fight (clean in every view, like a whole highlight).
        const highlight = highlightedContoursRef.current.get(entityId);
        const source = highlight === null || highlight === void 0 ? void 0 : highlight.entity;
        let remMaterial;
        let remWidth;
        if (source === null || source === void 0 ? void 0 : source.polyline) {
            remMaterial = source.polyline.material;
            remWidth = source.polyline.width;
            source.polyline.show = new Cesium.ConstantProperty(false);
            // Claim ownership so the layer's restyle effect doesn't re-show/restyle it.
            source[HIGHLIGHTED_CONTOUR_FLAG] = true;
        }
        const overlay = ds.entities.getById(entityId);
        if (overlay === null || overlay === void 0 ? void 0 : overlay.polyline) {
            overlay.polyline.positions = new Cesium.ConstantProperty(slicePolylineByFraction(positions, start, end));
        }
        else {
            // Whole highlights carry no overlay (the source line is restyled in
            // place), so entering Partial mode creates the slice here.
            ds.entities.add({
                id: entityId,
                polyline: {
                    positions: slicePolylineByFraction(positions, start, end),
                    material: new Cesium.ColorMaterialProperty((_a = highlight === null || highlight === void 0 ? void 0 : highlight.highlightColor) !== null && _a !== void 0 ? _a : Cesium.Color.fromCssColorString(DEFAULT_HIGHLIGHT_COLOR)),
                    width: (_b = highlight === null || highlight === void 0 ? void 0 : highlight.userLineWidth) !== null && _b !== void 0 ? _b : DEFAULT_HIGHLIGHT_LINE_WIDTH,
                    clampToGround: isTerrainOn(viewer),
                    arcType: Cesium.ArcType.GEODESIC,
                    zIndex: CONTOUR_HIGHLIGHT_ZINDEX
                }
            });
        }
        // Remainder in original style — closed loops have one complement arc,
        // open lines have the two end pieces.
        const remSlices = isClosedPolyline(positions)
            ? [slicePolylineByFraction(positions, end, start)]
            : [slicePolylineByFraction(positions, 0, start), slicePolylineByFraction(positions, end, 1)];
        for (let i = 0; i < 2; i++) {
            const remId = `${entityId}::rem::${i}`;
            const pts = remSlices[i];
            const existingRem = ds.entities.getById(remId);
            if (!(source === null || source === void 0 ? void 0 : source.polyline) || !pts || pts.length < 2) {
                if (existingRem)
                    ds.entities.remove(existingRem);
                continue;
            }
            if (existingRem === null || existingRem === void 0 ? void 0 : existingRem.polyline) {
                existingRem.polyline.positions = new Cesium.ConstantProperty(pts);
            }
            else {
                ds.entities.add({
                    id: remId,
                    polyline: {
                        positions: pts,
                        material: remMaterial,
                        width: remWidth,
                        clampToGround: isTerrainOn(viewer),
                        arcType: Cesium.ArcType.GEODESIC,
                    },
                });
            }
        }
        const startPos = interpolateAlongCartesianPolyline(positions, start);
        const endPos = interpolateAlongCartesianPolyline(positions, end);
        const startHandle = ds.entities.getById(entityId + HANDLE_START_SUFFIX);
        if (startHandle && startPos)
            startHandle.position = new Cesium.ConstantPositionProperty(startPos);
        const endHandle = ds.entities.getById(entityId + HANDLE_END_SUFFIX);
        if (endHandle && endPos)
            endHandle.position = new Cesium.ConstantPositionProperty(endPos);
    }, [viewer, getSourcePositions]);
    // Create (or move) the two handle marker entities for a highlight.
    const ensurePartialHandles = useCallback((entityId, start, end) => {
        var _a;
        const ds = highlightsDataSourceRef.current;
        if (!ds || !viewer || viewer.isDestroyed())
            return;
        const positions = getSourcePositions(entityId);
        if (!positions)
            return;
        const highlight = highlightedContoursRef.current.get(entityId);
        const baseColor = (_a = highlight === null || highlight === void 0 ? void 0 : highlight.highlightColor) !== null && _a !== void 0 ? _a : Cesium.Color.fromCssColorString(DEFAULT_HIGHLIGHT_COLOR);
        const makeHandle = (suffix, fraction) => {
            const id = entityId + suffix;
            if (ds.entities.getById(id))
                return;
            const pos = interpolateAlongCartesianPolyline(positions, fraction);
            if (!pos)
                return;
            ds.entities.add({
                id,
                position: pos,
                point: {
                    pixelSize: 13,
                    color: Cesium.Color.WHITE,
                    outlineColor: baseColor,
                    outlineWidth: 3,
                    // Always visible — the handles must sit on top of terrain and the
                    // contour line so they stay grabbable regardless of camera tilt.
                    // Clamped so they ride the terrain with the highlight line when 3D
                    // terrain is on (and sit on the ellipsoid, identically to before,
                    // when it's off); disableDepthTestDistance keeps them on top.
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                },
            });
        };
        makeHandle(HANDLE_START_SUFFIX, start);
        makeHandle(HANDLE_END_SUFFIX, end);
        applyPartialRender(entityId, start, end);
    }, [viewer, getSourcePositions, applyPartialRender]);
    // Switch the pending contour into Partial mode: keep an already-partial
    // range as-is, otherwise seed a centered default span. Spawns the handles.
    const enablePartialMode = useCallback(() => {
        var _a, _b;
        const entityId = (pendingClick === null || pendingClick === void 0 ? void 0 : pendingClick.entity.id) || '';
        if (!entityId)
            return;
        const highlight = highlightedContoursRef.current.get(entityId);
        if (!highlight)
            return;
        let start = (_a = highlight.startFraction) !== null && _a !== void 0 ? _a : 0;
        let end = (_b = highlight.endFraction) !== null && _b !== void 0 ? _b : 1;
        if (!isPartialRange(start, end)) {
            start = DEFAULT_PARTIAL_START;
            end = DEFAULT_PARTIAL_END;
        }
        // Switching to partial: the whole line should no longer be recoloured —
        // restore the source contour; the partial slice overlay takes over.
        restoreSourceContour(highlight.entity);
        partialFractionsRef.current.set(entityId, { start, end });
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            const h = newMap.get(entityId);
            if (h)
                newMap.set(entityId, { ...h, startFraction: start, endFraction: end });
            return newMap;
        });
        setPartialEntityId(entityId);
        ensurePartialHandles(entityId, start, end);
    }, [pendingClick, ensurePartialHandles]);
    // Switch the pending contour back to a whole highlight: drop the handles
    // and restore the full 0→1 range.
    const disablePartialMode = useCallback(() => {
        var _a, _b;
        const entityId = (pendingClick === null || pendingClick === void 0 ? void 0 : pendingClick.entity.id) || '';
        if (!entityId)
            return;
        // Leaving partial → drop the remainder pieces and re-show the source line
        // (it gets recoloured for the whole highlight below).
        if (highlightsDataSourceRef.current) {
            for (let i = 0; i < 2; i++) {
                const rem = highlightsDataSourceRef.current.entities.getById(`${entityId}::rem::${i}`);
                if (rem)
                    highlightsDataSourceRef.current.entities.remove(rem);
            }
        }
        const srcEntity = (_a = highlightedContoursRef.current.get(entityId)) === null || _a === void 0 ? void 0 : _a.entity;
        if (srcEntity === null || srcEntity === void 0 ? void 0 : srcEntity.polyline)
            srcEntity.polyline.show = new Cesium.ConstantProperty(true);
        removePartialHandles(entityId);
        setPartialEntityId(null);
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            const h = newMap.get(entityId);
            if (h)
                newMap.set(entityId, { ...h, startFraction: 0, endFraction: 1 });
            return newMap;
        });
        // Back to whole: the recoloured source line IS the highlight — drop the
        // slice overlay entirely rather than stretching it to full length on top.
        const ds = highlightsDataSourceRef.current;
        const overlay = ds === null || ds === void 0 ? void 0 : ds.entities.getById(entityId);
        if (overlay)
            ds.entities.remove(overlay);
        const highlight = highlightedContoursRef.current.get(entityId);
        if (highlight) {
            restyleSourceContour(highlight.entity, highlight.highlightColor, (_b = highlight.userLineWidth) !== null && _b !== void 0 ? _b : DEFAULT_HIGHLIGHT_LINE_WIDTH);
        }
    }, [pendingClick, removePartialHandles]);
    // Flip which of the two arcs (for a closed loop) is highlighted by
    // swapping the start/end fractions — slicePolylineByFraction renders the
    // complementary arc when start > end.
    const flipPartialSide = useCallback(() => {
        const entityId = (pendingClick === null || pendingClick === void 0 ? void 0 : pendingClick.entity.id) || '';
        if (!entityId)
            return;
        const current = partialFractionsRef.current.get(entityId);
        if (!current)
            return;
        const flipped = { start: current.end, end: current.start };
        partialFractionsRef.current.set(entityId, flipped);
        applyPartialRender(entityId, flipped.start, flipped.end);
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            const h = newMap.get(entityId);
            if (h)
                newMap.set(entityId, { ...h, startFraction: flipped.start, endFraction: flipped.end });
            return newMap;
        });
    }, [pendingClick, applyPartialRender]);
    // LEFT_DOWN: begin dragging a handle if the click landed on one. Returns
    // true (and disables the camera) so the router stops here and Cesium
    // doesn't pan the globe mid-drag.
    const handleMapMouseDown = useCallback((event) => {
        var _a;
        if (!viewer || viewer.isDestroyed() || !partialEntityId)
            return false;
        const picked = viewer.scene.pick(event.position);
        const id = (_a = picked === null || picked === void 0 ? void 0 : picked.id) === null || _a === void 0 ? void 0 : _a.id;
        if (typeof id !== 'string')
            return false;
        let which = null;
        if (id === partialEntityId + HANDLE_START_SUFFIX)
            which = 'start';
        else if (id === partialEntityId + HANDLE_END_SUFFIX)
            which = 'end';
        if (!which)
            return false;
        draggingHandleRef.current = { entityId: partialEntityId, which };
        disableCameraControls(viewer);
        return true;
    }, [viewer, partialEntityId]);
    // Drive a handle drag from MOUSE_MOVE: project the cursor onto the source
    // contour, snap the dragged handle to that fraction (keeping the minimum
    // gap), and re-slice the overlay live. Invoked via handleDragMoveRef from
    // handleMapHover. Returns true while a drag is active.
    const onHandleDragMove = useCallback((endPosition) => {
        const drag = draggingHandleRef.current;
        if (!drag || !viewer || viewer.isDestroyed())
            return false;
        const positions = getSourcePositions(drag.entityId);
        const fractions = partialFractionsRef.current.get(drag.entityId);
        if (!positions || !fractions)
            return false;
        const ray = viewer.camera.getPickRay(endPosition);
        const world = ray ? viewer.scene.globe.pick(ray, viewer.scene) : null;
        if (!world)
            return true; // still dragging, just off-globe this frame
        let fraction = nearestFractionOnPolyline(positions, world);
        const other = drag.which === 'start' ? fractions.end : fractions.start;
        if (isClosedPolyline(positions)) {
            // Closed loop: handles may cross (that just re-orients which arc is lit,
            // same as Flip). Only stop them coinciding into a degenerate slice.
            if (Math.abs(fraction - other) < MIN_PARTIAL_GAP) {
                fraction = fraction >= other
                    ? Math.min(1, other + MIN_PARTIAL_GAP)
                    : Math.max(0, other - MIN_PARTIAL_GAP);
            }
        }
        else if (drag.which === 'start') {
            // Open contour: keep start < end so the slice stays a single arc.
            fraction = Math.max(0, Math.min(fraction, other - MIN_PARTIAL_GAP));
        }
        else {
            fraction = Math.min(1, Math.max(fraction, other + MIN_PARTIAL_GAP));
        }
        const next = drag.which === 'start'
            ? { start: fraction, end: fractions.end }
            : { start: fractions.start, end: fraction };
        partialFractionsRef.current.set(drag.entityId, next);
        applyPartialRender(drag.entityId, next.start, next.end);
        return true;
    }, [viewer, getSourcePositions, applyPartialRender]);
    handleDragMoveRef.current = onHandleDragMove;
    // LEFT_UP: finish a handle drag — commit the live fractions to React state
    // and re-enable the camera.
    const handleMapMouseUp = useCallback(() => {
        const drag = draggingHandleRef.current;
        if (!drag)
            return;
        draggingHandleRef.current = null;
        if (viewer && !viewer.isDestroyed())
            enableCameraControls(viewer);
        const fractions = partialFractionsRef.current.get(drag.entityId);
        if (!fractions)
            return;
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            const h = newMap.get(drag.entityId);
            if (h)
                newMap.set(drag.entityId, { ...h, startFraction: fractions.start, endFraction: fractions.end });
            return newMap;
        });
    }, [viewer]);
    // Tracks the entity currently being previewed via the dialog's color
    // picker. Set when a color is first picked (the entity is highlighted
    // immediately so the user can see the colour live). Cleared when the
    // user confirms or cancels.
    const previewingEntityIdRef = useRef(null);
    // Apply the default-colour preview as soon as a click sets pendingClick
    // for an UNHIGHLIGHTED contour, so the user sees the highlight
    // immediately on click rather than only after they touch the colour
    // picker. Skipped if a preview is already active for that entity, or
    // if the entity is already highlighted (edit mode — values are
    // pre-filled from the existing highlight, no preview to apply).
    useEffect(() => {
        if (!pendingClick)
            return;
        const entityId = pendingClick.entity.id || '';
        if (previewingEntityIdRef.current === entityId)
            return;
        if (highlightedContours.has(entityId))
            return;
        highlightContour(pendingClick.entity, pendingClick.artifactId, pendingClick.contourInterval, pendingClick.contourElevation, DEFAULT_HIGHLIGHT_COLOR);
        previewingEntityIdRef.current = entityId;
    }, [pendingClick, highlightContour, highlightedContours]);
    // Update the colour of an existing highlight by rewriting its overlay
    // entity's material. Source contour is not touched. colorHex is also
    // stored on the highlight so a subsequent edit pre-fills the picker
    // with the right swatch.
    const updateHighlightColor = useCallback((entityId, colorHex) => {
        var _a, _b, _c;
        const highlight = highlightedContours.get(entityId);
        if (!highlight)
            return;
        const newColor = Cesium.Color.fromCssColorString(colorHex);
        if (highlightsDataSourceRef.current) {
            const overlayEntity = highlightsDataSourceRef.current.entities.getById(entityId);
            if (overlayEntity === null || overlayEntity === void 0 ? void 0 : overlayEntity.polyline) {
                overlayEntity.polyline.material = new Cesium.ColorMaterialProperty(newColor);
                overlayEntity.polyline.depthFailMaterial = new Cesium.ColorMaterialProperty(newColor);
            }
        }
        // Keep the recoloured source contour (whole highlight) in sync.
        if (isWholeSpan((_a = highlight.startFraction) !== null && _a !== void 0 ? _a : 0, (_b = highlight.endFraction) !== null && _b !== void 0 ? _b : 1) &&
            ((_c = highlight.entity) === null || _c === void 0 ? void 0 : _c.polyline)) {
            highlight.entity.polyline.material = new Cesium.ColorMaterialProperty(newColor);
        }
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            const h = newMap.get(entityId);
            if (h)
                newMap.set(entityId, { ...h, highlightColor: newColor, colorHex });
            return newMap;
        });
    }, [highlightedContours]);
    // Apply (or update) a colour preview for the contour the dialog is
    // currently open over. Works for both NEW highlights (first colour
    // pick creates the overlay) and EDIT highlights (updates the existing
    // overlay's colour in place). The change becomes permanent on confirm
    // or is reverted on cancel.
    const previewColor = useCallback((colorHex) => {
        if (!pendingClick)
            return;
        const entityId = pendingClick.entity.id || '';
        if (highlightedContours.has(entityId)) {
            updateHighlightColor(entityId, colorHex);
            if (!editBaselineRef.current)
                previewingEntityIdRef.current = entityId;
        }
        else {
            highlightContour(pendingClick.entity, pendingClick.artifactId, pendingClick.contourInterval, pendingClick.contourElevation, colorHex);
            previewingEntityIdRef.current = entityId;
        }
    }, [pendingClick, highlightContour, updateHighlightColor, highlightedContours]);
    // Update the user-set line width of an existing highlight by
    // rewriting its overlay entity's polyline width. Source contour is
    // not touched.
    const updateHighlightLineWidth = useCallback((entityId, baseWidth) => {
        var _a, _b, _c;
        const highlight = highlightedContours.get(entityId);
        if (!highlight)
            return;
        if (highlightsDataSourceRef.current) {
            const overlayEntity = highlightsDataSourceRef.current.entities.getById(entityId);
            if (overlayEntity === null || overlayEntity === void 0 ? void 0 : overlayEntity.polyline) {
                overlayEntity.polyline.width = new Cesium.ConstantProperty(baseWidth);
            }
        }
        // Keep the recoloured source contour (whole highlight) in sync.
        if (isWholeSpan((_a = highlight.startFraction) !== null && _a !== void 0 ? _a : 0, (_b = highlight.endFraction) !== null && _b !== void 0 ? _b : 1) &&
            ((_c = highlight.entity) === null || _c === void 0 ? void 0 : _c.polyline)) {
            highlight.entity.polyline.width = new Cesium.ConstantProperty(baseWidth);
        }
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            const h = newMap.get(entityId);
            if (h)
                newMap.set(entityId, { ...h, userLineWidth: baseWidth });
            return newMap;
        });
    }, [highlightedContours]);
    // Apply (or update) a line-width preview, mirroring previewColor.
    // Works for both NEW and EDIT highlights — updates in place if the
    // entity is already highlighted, otherwise lands a fresh default-red
    // highlight first and then layers the chosen width on top.
    const previewLineWidth = useCallback((baseWidth) => {
        if (!pendingClick)
            return;
        const entityId = pendingClick.entity.id || '';
        if (highlightedContours.has(entityId)) {
            updateHighlightLineWidth(entityId, baseWidth);
            if (!editBaselineRef.current)
                previewingEntityIdRef.current = entityId;
        }
        else {
            highlightContour(pendingClick.entity, pendingClick.artifactId, pendingClick.contourInterval, pendingClick.contourElevation, DEFAULT_HIGHLIGHT_COLOR);
            previewingEntityIdRef.current = entityId;
            updateHighlightLineWidth(entityId, baseWidth);
        }
    }, [pendingClick, highlightContour, updateHighlightLineWidth, highlightedContours]);
    // Confirm highlight from dialog. Three cases:
    //  - Edit mode: colour/width were applied in place against the live
    //    highlight; just persist any name change and clear the baseline.
    //  - Preview was applied (new highlight created via colour or width
    //    pick before confirm): persist the name; the colour and width are
    //    already in place.
    //  - Neither (user opened the dialog and clicked confirm without ever
    //    touching the picker — currently can't happen since the click
    //    auto-applies a default red preview, but kept as a fallback).
    const confirmHighlight = useCallback((colorHex = DEFAULT_HIGHLIGHT_COLOR, name) => {
        var _a;
        if (!pendingClick)
            return null;
        const entityId = pendingClick.entity.id || '';
        if (((_a = editBaselineRef.current) === null || _a === void 0 ? void 0 : _a.entityId) === entityId || previewingEntityIdRef.current === entityId) {
            if (name) {
                setHighlightedContours(prev => {
                    const newMap = new Map(prev);
                    const h = newMap.get(entityId);
                    if (h)
                        newMap.set(entityId, { ...h, name });
                    return newMap;
                });
            }
            editBaselineRef.current = null;
            previewingEntityIdRef.current = null;
        }
        else {
            highlightContour(pendingClick.entity, pendingClick.artifactId, pendingClick.contourInterval, pendingClick.contourElevation, colorHex, name);
        }
        // The sliced overlay stays; only the draggable handles come down on
        // confirm. Fractions already live on the highlight (and so flow into
        // the snapshot below).
        removePartialHandles(entityId);
        if (partialEntityId === entityId)
            setPartialEntityId(null);
        setPendingClick(null);
        // Build a snapshot of the just-committed highlight so the caller
        // can promote it to a folder without re-reading React state (which
        // hasn't re-rendered yet — see Bug 1).
        const existing = highlightedContours.get(entityId);
        if (!existing && !entityId)
            return null;
        const snapshot = existing
            ? { ...existing, name: name !== null && name !== void 0 ? name : existing.name, colorHex }
            : {
                // Best-effort reconstruction when the contour is being
                // committed for the first time — fields mirror applyHighlight.
                entityId,
                entity: pendingClick.entity,
                name: name !== null && name !== void 0 ? name : `Contour at ${pendingClick.contourElevation || 'unknown'}`,
                contourInterval: pendingClick.contourInterval,
                contourElevation: pendingClick.contourElevation,
                highlightColor: Cesium.Color.fromCssColorString(colorHex),
                colorHex,
                artifactId: pendingClick.artifactId,
                coordinates: [],
                startFraction: 0,
                endFraction: 1,
            };
        return { entityId, snapshot };
    }, [pendingClick, highlightContour, highlightedContours, removePartialHandles, partialEntityId]);
    // Discard the current highlight outright. In create mode this drops
    // the preview that was applied on click; in edit mode it removes the
    // existing highlight entirely. Backs the dialog's "Discard" action
    // (the dialog wraps this with a confirm in edit mode).
    const discardHighlight = useCallback(() => {
        if (editBaselineRef.current) {
            const { entityId } = editBaselineRef.current;
            unhighlightContour(entityId);
            editBaselineRef.current = null;
        }
        else if (previewingEntityIdRef.current) {
            unhighlightContour(previewingEntityIdRef.current);
            previewingEntityIdRef.current = null;
        }
        setPendingClick(null);
    }, [unhighlightContour]);
    // Cancel highlight dialog. Two cases:
    //  - Edit mode: restore the highlight's pre-edit colour, width, and
    //    name from the snapshot taken at click time.
    //  - Preview (new): drop the freshly-applied highlight entirely.
    const cancelHighlight = useCallback(() => {
        if (editBaselineRef.current) {
            const { entityId, colorHex, userLineWidth, name, startFraction, endFraction } = editBaselineRef.current;
            updateHighlightColor(entityId, colorHex);
            updateHighlightLineWidth(entityId, userLineWidth !== null && userLineWidth !== void 0 ? userLineWidth : DEFAULT_HIGHLIGHT_LINE_WIDTH);
            // Restore the pre-edit representation and drop the handles — the
            // partial range could have been dragged or toggled during the
            // cancelled edit.
            removePartialHandles(entityId);
            if (partialEntityId === entityId)
                setPartialEntityId(null);
            const highlight = highlightedContoursRef.current.get(entityId);
            if (isWholeSpan(startFraction, endFraction)) {
                // Whole baseline: the restyled source line is the highlight — drop
                // any slice + remainder pieces left by a partial preview and re-show
                // the source with the pre-edit look.
                const ds = highlightsDataSourceRef.current;
                if (ds) {
                    const overlay = ds.entities.getById(entityId);
                    if (overlay)
                        ds.entities.remove(overlay);
                    for (let i = 0; i < 2; i++) {
                        const rem = ds.entities.getById(`${entityId}::rem::${i}`);
                        if (rem)
                            ds.entities.remove(rem);
                    }
                }
                if (highlight === null || highlight === void 0 ? void 0 : highlight.entity) {
                    if (highlight.entity.polyline) {
                        highlight.entity.polyline.show = new Cesium.ConstantProperty(true);
                    }
                    restyleSourceContour(highlight.entity, Cesium.Color.fromCssColorString(colorHex), userLineWidth !== null && userLineWidth !== void 0 ? userLineWidth : DEFAULT_HIGHLIGHT_LINE_WIDTH);
                }
            }
            else {
                // Partial baseline: re-render the pre-edit slice + remainder pieces.
                partialFractionsRef.current.set(entityId, { start: startFraction, end: endFraction });
                applyPartialRender(entityId, startFraction, endFraction);
            }
            setHighlightedContours(prev => {
                const newMap = new Map(prev);
                const h = newMap.get(entityId);
                if (h)
                    newMap.set(entityId, { ...h, name, startFraction, endFraction });
                return newMap;
            });
            editBaselineRef.current = null;
        }
        else if (previewingEntityIdRef.current) {
            unhighlightContour(previewingEntityIdRef.current);
            previewingEntityIdRef.current = null;
        }
        setPendingClick(null);
    }, [unhighlightContour, updateHighlightColor, updateHighlightLineWidth, removePartialHandles, partialEntityId, applyPartialRender]);
    // Overlay visibility used to track source layer visibility (because
    // the highlight was applied by mutating the source). With the overlay
    // architecture the highlight is independent of the source layer and
    // always renders, so this effect is no longer needed.
    // Helper to apply a single highlight (used by both click and restore)
    // Restore-from-config path. Same overlay-only rendering as the
    // user-initiated highlightContour, but doesn't dedupe / replace
    // existing entries — pendingRestores handles that upstream.
    const applyHighlight = useCallback((entity, artifactId, contourInterval, contourElevation, color, name, startFraction = 0, endFraction = 1, lineWidth = DEFAULT_HIGHLIGHT_LINE_WIDTH) => {
        var _a, _b;
        const entityId = entity.id || `entity-${Date.now()}`;
        const positions = (_b = (_a = entity.polyline) === null || _a === void 0 ? void 0 : _a.positions) === null || _b === void 0 ? void 0 : _b.getValue(Cesium.JulianDate.now());
        if (!positions || positions.length < 2)
            return entityId;
        const coordinates = positions.map((pos) => {
            const cart = Cesium.Cartographic.fromCartesian(pos);
            return {
                lat: Cesium.Math.toDegrees(cart.latitude),
                lng: Cesium.Math.toDegrees(cart.longitude)
            };
        });
        const highlightColor = Cesium.Color.fromCssColorString(color);
        const overlayPositions = slicePolylineByFraction(positions, startFraction, endFraction);
        if (highlightsDataSourceRef.current) {
            highlightsDataSourceRef.current.entities.add({
                id: entityId,
                polyline: {
                    positions: overlayPositions,
                    width: lineWidth,
                    material: new Cesium.ColorMaterialProperty(highlightColor),
                    // depthFailMaterial keeps the restored highlight visible over the
                    // still-shown source line while unclamped (terrain off); zIndex
                    // does the same job while clamped (terrain on).
                    depthFailMaterial: new Cesium.ColorMaterialProperty(highlightColor),
                    clampToGround: viewer ? isTerrainOn(viewer) : false,
                    arcType: Cesium.ArcType.GEODESIC,
                    zIndex: CONTOUR_HIGHLIGHT_ZINDEX
                }
            });
        }
        const highlightName = name || `Contour at ${contourElevation || 'unknown'}`;
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            newMap.set(entityId, {
                entityId,
                entity,
                name: highlightName,
                artifactId,
                contourInterval,
                contourElevation,
                highlightColor,
                colorHex: color,
                userLineWidth: lineWidth,
                coordinates,
                startFraction,
                endFraction
            });
            return newMap;
        });
        return entityId;
    }, [viewer]);
    // Open the highlight dialog over a server-saved contour artifact.
    // The saved geometry has coordinates but no live source layer, so we
    // synthesize a polyline overlay and seed the in-memory state. The
    // existing edit/preview/save machinery + editingState routing then
    // takes care of updating the server artifact on Save.
    const startEditFromArtifact = useCallback((geometry, name, color, serverArtifactId) => {
        var _a;
        if (!viewer || viewer.isDestroyed() || !highlightsDataSourceRef.current)
            return null;
        const coordinates = Array.isArray(geometry.coordinates)
            ? geometry.coordinates
            : [];
        if (coordinates.length < 2)
            return null;
        const entityId = geometry.entity_id || `synth-contour-${serverArtifactId}`;
        const contourInterval = String((_a = geometry.contour_interval) !== null && _a !== void 0 ? _a : '');
        const contourElevation = geometry.elevation ? String(geometry.elevation) : null;
        const highlightColor = Cesium.Color.fromCssColorString(color);
        const positions = coordinates.map(c => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
        const startFraction = typeof geometry.start_fraction === 'number' ? geometry.start_fraction : 0;
        const endFraction = typeof geometry.end_fraction === 'number' ? geometry.end_fraction : 1;
        const lineWidth = typeof geometry.line_width === 'number' ? geometry.line_width : DEFAULT_HIGHLIGHT_LINE_WIDTH;
        // Re-create the overlay if it already exists. Render the saved slice
        // (full line when the highlight was whole) at the saved width.
        const existing = highlightsDataSourceRef.current.entities.getById(entityId);
        if (existing)
            highlightsDataSourceRef.current.entities.remove(existing);
        const overlayEntity = highlightsDataSourceRef.current.entities.add({
            id: entityId,
            polyline: {
                positions: slicePolylineByFraction(positions, startFraction, endFraction),
                width: lineWidth,
                material: new Cesium.ColorMaterialProperty(highlightColor),
                depthFailMaterial: new Cesium.ColorMaterialProperty(highlightColor),
                clampToGround: isTerrainOn(viewer),
                arcType: Cesium.ArcType.GEODESIC,
                zIndex: CONTOUR_HIGHLIGHT_ZINDEX,
            },
        });
        setHighlightedContours(prev => {
            const newMap = new Map(prev);
            newMap.set(entityId, {
                entityId,
                entity: overlayEntity,
                name,
                artifactId: serverArtifactId,
                contourInterval,
                contourElevation,
                highlightColor,
                colorHex: color,
                userLineWidth: lineWidth,
                coordinates,
                startFraction,
                endFraction,
            });
            return newMap;
        });
        editBaselineRef.current = {
            entityId,
            colorHex: color,
            userLineWidth: lineWidth,
            name,
            startFraction,
            endFraction,
        };
        setPendingClick({
            entity: overlayEntity,
            contourInterval,
            contourElevation,
            screenPosition: { x: window.innerWidth / 2 - 170, y: window.innerHeight / 2 - 110 },
            artifactId: serverArtifactId,
        });
        return entityId;
    }, [viewer]);
    const findEntityById = useCallback((entityId, artifactId) => {
        var _a;
        const artifact = loadedArtifacts.get(artifactId);
        if (!artifact || artifact.type !== 'vector')
            return null;
        const dataSource = artifact.cesiumObject;
        if (!((_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values))
            return null;
        for (const entity of dataSource.entities.values) {
            if (entity.id === entityId) {
                return entity;
            }
        }
        return null;
    }, [loadedArtifacts]);
    const findEntityByElevationAndHash = useCallback((artifactId, targetElevation, targetHash) => {
        var _a;
        const artifact = loadedArtifacts.get(artifactId);
        if (!artifact || artifact.type !== 'vector')
            return null;
        const dataSource = artifact.cesiumObject;
        if (!((_a = dataSource === null || dataSource === void 0 ? void 0 : dataSource.entities) === null || _a === void 0 ? void 0 : _a.values))
            return null;
        const targetNumMatch = targetElevation.match(/([\d.]+)/);
        const targetNum = targetNumMatch ? parseFloat(targetNumMatch[1]) : null;
        const candidates = [];
        for (const entity of dataSource.entities.values) {
            const entityElev = extractElevationFromEntity(entity);
            if (!entityElev)
                continue;
            const entityNumMatch = entityElev.match(/([\d.]+)/);
            const entityNum = entityNumMatch ? parseFloat(entityNumMatch[1]) : null;
            const elevationMatches = (targetNum !== null && entityNum !== null && Math.abs(targetNum - entityNum) < 0.1) ||
                entityElev === targetElevation;
            if (elevationMatches) {
                if (targetHash) {
                    if (computeCoordHash(entity) === targetHash) {
                        return entity;
                    }
                    candidates.push(entity);
                }
                else {
                    return entity;
                }
            }
        }
        return candidates.length > 0 ? candidates[0] : null;
    }, [loadedArtifacts]);
    // Process pending restores when artifacts are loaded
    useEffect(() => {
        var _a, _b, _c, _d;
        if (pendingRestores.length === 0)
            return;
        const stillPending = [];
        for (const hl of pendingRestores) {
            // Check if this highlight is already applied
            const existingHighlight = Array.from(highlightedContours.values()).find(h => h.artifactId === hl.artifactId && h.contourElevation === hl.elevation);
            if (existingHighlight) {
                continue;
            }
            // Check if artifact is loaded
            const artifact = loadedArtifacts.get(hl.artifactId);
            if (!artifact) {
                stillPending.push(hl);
                continue;
            }
            // Must be a vector artifact with a data source
            if (artifact.type !== 'vector') {
                console.warn(`[ContourHighlight] Artifact ${hl.artifactId} is not a vector type (${artifact.type}), skipping`);
                continue;
            }
            // Parse entityId for elevation|hash format (new format) or fall back to elevation field
            let targetElevation = hl.elevation;
            let targetHash;
            if ((_a = hl.entityId) === null || _a === void 0 ? void 0 : _a.includes('|')) {
                const [storedElevation, storedHash] = hl.entityId.split('|');
                if (storedElevation)
                    targetElevation = storedElevation;
                if (storedHash)
                    targetHash = storedHash;
            }
            let entity = null;
            if (targetElevation) {
                entity = findEntityByElevationAndHash(hl.artifactId, targetElevation, targetHash);
            }
            if (!entity) {
                continue;
            }
            applyHighlight(entity, hl.artifactId, hl.contourInterval, hl.elevation || null, hl.color, hl.name, (_b = hl.startFraction) !== null && _b !== void 0 ? _b : 0, (_c = hl.endFraction) !== null && _c !== void 0 ? _c : 1, (_d = hl.lineWidth) !== null && _d !== void 0 ? _d : DEFAULT_HIGHLIGHT_LINE_WIDTH);
        }
        if (stillPending.length !== pendingRestores.length) {
            setPendingRestores(stillPending);
        }
    }, [loadedArtifacts, pendingRestores, highlightedContours, applyHighlight, findEntityByElevationAndHash]);
    const restoreFromConfig = useCallback((highlights, isRetry = false) => {
        // Clear existing highlights (only on initial call, not retries - though we don't use retries anymore)
        if (!isRetry) {
            unhighlightAll();
        }
        // Store highlights as pending - the useEffect watching loadedArtifacts will apply them
        setPendingRestores(highlights);
    }, [unhighlightAll]);
    // Subscribe to scope events for clear and restore operations
    useEventSubscribe('scope:clearAll', () => {
        unhighlightAll();
    }, [unhighlightAll]);
    useEventSubscribe('scope:restore', (payload) => {
        if (payload.contourHighlights && payload.contourHighlights.length > 0) {
            restoreFromConfig(payload.contourHighlights);
        }
    }, [restoreFromConfig]);
    // Per-highlight visibility. A partial highlight toggles its slice +
    // remainder pieces and swaps the hidden source line back in, so hiding
    // the highlight brings the base contour back. A whole highlight is a
    // restyle of the source line itself, so visibility toggles the restyle.
    const toggleItemVisibility = useCallback((id) => {
        var _a, _b, _c, _d;
        const highlight = highlightedContours.get(id);
        if (!highlight)
            return;
        const ds = highlightsDataSourceRef.current;
        const currentlyVisible = contourVisibility.has(id) ? contourVisibility.get(id) : true;
        const newVisible = !currentlyVisible;
        const overlayEntity = ds === null || ds === void 0 ? void 0 : ds.entities.getById(id);
        if (overlayEntity) {
            overlayEntity.show = newVisible;
            let split = false;
            for (let i = 0; i < 2; i++) {
                const rem = ds.entities.getById(`${id}::rem::${i}`);
                if (rem) {
                    rem.show = newVisible;
                    split = true;
                }
            }
            // A split hid the source line — bring it back while the highlight is
            // hidden so the contour itself doesn't vanish with it.
            if (split && ((_a = highlight.entity) === null || _a === void 0 ? void 0 : _a.polyline)) {
                highlight.entity.polyline.show = new Cesium.ConstantProperty(!newVisible);
            }
        }
        else if ((_b = highlight.entity) === null || _b === void 0 ? void 0 : _b.polyline) {
            if (newVisible) {
                const opacity = (_c = contourOpacities.get(id)) !== null && _c !== void 0 ? _c : 1.0;
                restyleSourceContour(highlight.entity, highlight.highlightColor.withAlpha(opacity), (_d = highlight.userLineWidth) !== null && _d !== void 0 ? _d : DEFAULT_HIGHLIGHT_LINE_WIDTH);
            }
            else {
                restoreSourceContour(highlight.entity);
            }
        }
        setContourVisibility(prev => { const m = new Map(prev); m.set(id, newVisible); return m; });
    }, [highlightedContours, contourVisibility, contourOpacities]);
    const isItemVisible = useCallback((id) => {
        if (contourVisibility.has(id))
            return contourVisibility.get(id);
        return highlightedContours.has(id);
    }, [contourVisibility, highlightedContours]);
    const updateContourOpacity = useCallback((id, opacity) => {
        var _a, _b;
        const highlight = highlightedContours.get(id);
        if (!highlight)
            return;
        const faded = highlight.highlightColor.withAlpha(opacity);
        const overlayEntity = (_a = highlightsDataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.getById(id);
        if (overlayEntity === null || overlayEntity === void 0 ? void 0 : overlayEntity.polyline) {
            overlayEntity.polyline.material = new Cesium.ColorMaterialProperty(faded);
            overlayEntity.polyline.depthFailMaterial = new Cesium.ColorMaterialProperty(faded);
        }
        else if (((_b = highlight.entity) === null || _b === void 0 ? void 0 : _b.polyline) &&
            highlight.entity[HL_SOURCE_STASH] !== undefined) {
            // Whole highlight (restyled source line, no overlay) — fade the restyle.
            highlight.entity.polyline.material = new Cesium.ColorMaterialProperty(faded);
        }
        setContourOpacities(prev => { const m = new Map(prev); m.set(id, opacity); return m; });
    }, [highlightedContours]);
    const getItemOpacity = useCallback((id) => {
        var _a;
        return (_a = contourOpacities.get(id)) !== null && _a !== void 0 ? _a : 1.0;
    }, [contourOpacities]);
    const updateContourName = useCallback((id, name) => {
        setHighlightedContours(prev => {
            const highlight = prev.get(id);
            if (!highlight)
                return prev;
            const newMap = new Map(prev);
            newMap.set(id, { ...highlight, name });
            return newMap;
        });
    }, []);
    // Pre-fill values for the dialog when re-opening over an already-
    // highlighted contour. Null when the dialog is creating a new
    // highlight (no existing values to pre-fill).
    const editingDefaults = pendingClick
        ? (() => {
            var _a;
            const existing = highlightedContours.get(pendingClick.entity.id || '');
            if (!existing)
                return null;
            return {
                color: existing.colorHex,
                lineWidth: (_a = existing.userLineWidth) !== null && _a !== void 0 ? _a : DEFAULT_HIGHLIGHT_LINE_WIDTH,
                name: existing.name,
            };
        })()
        : null;
    // Drives the dialog's Whole / Partial toggle + Flip control for the
    // contour the dialog is currently open over. `isPartial` initializes the
    // toggle; `isClosed` gates whether "Flip side" is offered (loops only).
    const partialInfo = pendingClick
        ? (() => {
            const entityId = pendingClick.entity.id || '';
            const existing = highlightedContours.get(entityId);
            const positions = getSourcePositions(entityId);
            return {
                isPartial: partialEntityId === entityId
                    || isPartialRange(existing === null || existing === void 0 ? void 0 : existing.startFraction, existing === null || existing === void 0 ? void 0 : existing.endFraction),
                isClosed: positions ? isClosedPolyline(positions) : false,
            };
        })()
        : null;
    return {
        highlightedContours,
        pendingClick,
        hoveredContour,
        handleMapClick,
        handleMapDoubleClick,
        handleMapHover,
        handleMapMouseDown,
        handleMapMouseUp,
        confirmHighlight,
        cancelHighlight,
        discardHighlight,
        startEditFromArtifact,
        previewColor,
        previewLineWidth,
        editingDefaults,
        partialInfo,
        enablePartialMode,
        disablePartialMode,
        flipPartialSide,
        unhighlightContour,
        unhighlightAll,
        restoreFromConfig,
        highlightCount: highlightedContours.size,
        toggleItemVisibility,
        isItemVisible,
        updateContourOpacity,
        getItemOpacity,
        updateContourName
    };
};
